import {GroupListProps, MatchParams} from '../../constants/types';
import {useRouteMatch} from 'react-router-dom';
import {useEffect, useState, useCallback} from 'react';
import {
  getAvailableProcessAssigneeGroups,
  getProcessDetailsById,
  getTasksDetailsByProcessId
} from '../../api/processes';
import Error from '../common/Error';
import Spinner from '../common/Spinner';
import Container from '../common/layout/Container';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  getTaskAlertSettingsByProcessType,
  getAlertSettingsByProcessType
} from '../../api/settings/alert_alarm';
import {AlertSettingsProps} from '../settings/alert_alarm/types';
import {TasksDetailsResponseProps} from './types';
import ProcessDetailsTimer from './ProcessDetailsTimer';
import ProcessTasks from './ProcessTasks';
import {DEFAULT_ERROR} from '../../api/constants';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch} from '../../store/hooks';
import {useTranslation} from 'react-i18next';
import {dateFormat} from '../../constants/constants';
import LiveTimeProgressBar from '../common/time_progress_bar/LiveTimeProgressBar';
import {calculateAndFormatTimeHHMMSS} from './helpers';
import {ProcessActionPopupData} from './types';
import ProcessAssigneeElement from './process_assignee/ProcessAssigneeElement';
import ProcessAssigneePopup from './process_assignee/ProcessAssigneePopup';
import {CurrentProcess} from '../tasks/types';

dayjs.extend(duration);

const ProcessDetails = () => {
  const {t} = useTranslation('processes');
  const dispatch = useAppDispatch();
  const {params}: MatchParams = useRouteMatch();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [processDetailsData, setProcessDetailsData] =
    useState<Nullable<CurrentProcess>>(null);
  const [tasksDetailsData, setTasksDetailsData] =
    useState<Nullable<TasksDetailsResponseProps>>(null);
  const [taskAlertData, setTaskAlertData] =
    useState<Nullable<AlertSettingsProps>>(null);
  const [processAlertData, setProcessAlertData] =
    useState<Nullable<AlertSettingsProps>>(null);
  const [isActionProcessing, setIsActionProcessing] = useState(false);
  const [processActionsPopupData, setProcessActionsPopupData] =
      useState<Nullable<ProcessActionPopupData>>(null);
  const [availableProcessAssigneeGroups, setAvailableProcessAssigneeGroups] =
      useState<Nullable<GroupListProps[]>>(null);

  const {id} = params || {};

  const {
    id: processId = 0,
    completedAt,
    targetTime,
    completedTasks,
    patient,
    startedAt,
    treatment,
    type,
    assignees = []
  } = processDetailsData || {};

  const formattedStartAt = dayjs(startedAt).format(dateFormat);

  const getProcessDetailsAndTasks = useCallback(async () => {
    setIsLoading(true);

    try {
      const [processDetailsResponse, tasksDetailsResponse] =
        await Promise.all([
          getProcessDetailsById(id),
          getTasksDetailsByProcessId(id)
        ]);
      setProcessDetailsData(processDetailsResponse);
      setTasksDetailsData(tasksDetailsResponse);
      const processTypeCode = processDetailsResponse?.type?.code;

      const [
        taskAlertRange,
        processAlertResponse,
        availableProcessAssigneeGroupsResponse
      ] = await Promise.all([
        getTaskAlertSettingsByProcessType(processTypeCode),
        getAlertSettingsByProcessType(processTypeCode),
        getAvailableProcessAssigneeGroups(processTypeCode)
      ]);

      setTaskAlertData(taskAlertRange);
      setProcessAlertData(processAlertResponse);
      setAvailableProcessAssigneeGroups(availableProcessAssigneeGroupsResponse);
    } catch (err) {
      setError(err.message || DEFAULT_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(setTitle(t('pageTitle.processDetails')));
  }, []);

  useEffect(() => {
    getProcessDetailsAndTasks();
  }, [getProcessDetailsAndTasks]);

  const getProcessDetailsResponse = useCallback(async () => {
    try {
      const processDetailsResponse = await getProcessDetailsById(id);
      setProcessDetailsData(processDetailsResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    }
  }, []);

  if (error) {
    return (<Error message={error}/>);
  }

  return (isLoading ? <Spinner loadingText={t('loadingText.processDetails')}/> :
    <Container>
      <div className="process-detail-heading">
        <div className="value-with-heading">
          <span className="heading">{t('processDetailsLabels.process')}</span>
          <span className="value">
            {type && type.description}
            {patient && ` ${patient.firstName} ${patient.lastName}`}
            {` ${formattedStartAt}`}
          </span>
        </div>
        <div className="value-with-heading">
          <ProcessAssigneeElement
            assignees={assignees}
            processId={processId}
            processData={processDetailsData}
            setProcessActionsPopupData={setProcessActionsPopupData}
            availableProcessAssigneeGroups={availableProcessAssigneeGroups}
          />
        </div>
        <div className="value-with-heading">
          <span className="heading">{t('processDetailsLabels.treatment')}</span>
          <span className="value">{treatment && treatment.displayName}</span>
        </div>
        <div className="value-with-heading">
          <span className="heading">{t('processDetailsLabels.taskStatus')}</span>
          <span className="value">
            {`Completed ${completedTasks || 0}`}
          </span>
        </div>
        <ProcessDetailsTimer
          startedAt={startedAt}
          completedAt={completedAt}
          totalTimeLabel={t('processDetailsLabels.totalTime')}
        />
        <LiveTimeProgressBar
          startedAt={startedAt || ''}
          completedAt={completedAt || ''}
          targetTime={targetTime || 0}
          alertSettings={processAlertData}
          isCurrentlyRunning={false}
          processExpiredTimeFormat={calculateAndFormatTimeHHMMSS}
        />
      </div>
      <ProcessTasks
        tasksDetailsData={tasksDetailsData}
        taskAlertData={taskAlertData}
      />
      <ProcessAssigneePopup
        processActionsPopupData={processActionsPopupData}
        setProcessActionsPopupData={setProcessActionsPopupData}
        isActionProcessing={isActionProcessing}
        setIsActionProcessing={setIsActionProcessing}
        getProcessData={getProcessDetailsResponse}
      />
    </Container>
  );
};

export default ProcessDetails;
